import type { ReactNode } from 'react'
import clsx from 'clsx'
import { InfoTooltip } from './InfoTooltip'

export interface NumericFieldProps {
  id: string
  label: ReactNode
  /** Explanatory InfoTooltip shown inside the label, after its text. */
  tooltip?: string
  /** Passed straight to the input. A number keeps React's loose
   * number-input comparison, so a partly typed "1." is not overwritten;
   * an optional field passes numberField(...)'s string, '' when unset. */
  value: string | number
  onChange: (raw: string) => void
  onBlur?: () => void
  step?: string
  min?: number
  max?: number
  /** Shown while the input is empty, e.g. the value that applies when unset. */
  placeholder?: string
  /** Turns the border red and is shown under the input. */
  error?: string
  /** Shown under the input, above any error. */
  helperText?: ReactNode
}

// The labeled numeric input every config form writes - label (optionally
// with a tooltip), <input type="number">, helper text, error. Parsing,
// defaults and validation stay with the caller: this only renders what it
// is given and reports the raw input string back (see lib/numberField.ts
// for the optional-number pair). PRD 14.
//
// A mouse wheel over a focused number input steps its value in most
// browsers, so scrolling the page past a field can silently change a saved
// setting. The input gives up focus on wheel instead, and the page scrolls.
export function NumericField({
  id,
  label,
  tooltip,
  value,
  onChange,
  onBlur,
  step,
  min,
  max,
  placeholder,
  error,
  helperText,
}: NumericFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label
        className={clsx(
          'text-meta font-medium text-text-secondary',
          tooltip && 'flex items-center gap-1',
        )}
        htmlFor={id}
      >
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </label>
      <input
        id={id}
        type="number"
        step={step}
        min={min}
        max={max}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        onWheel={(event) => event.currentTarget.blur()}
        className={clsx(
          'rounded-control border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled',
          error ? 'border-danger' : 'border-border',
        )}
      />
      {helperText}
      {error && <span className="text-meta text-danger">{error}</span>}
    </div>
  )
}
